import React, { JSX, useContext, useMemo } from "react";
import Head from "next/head";
import Image from "next/image";
import { LogContext } from "context/log";
import { Heading } from "@pepdirect/v3/ui/Heading";
import { ExternalLink } from "@pepdirect/v3/ui/ExternalLink";
import { PAGE_TYPE_ENUM } from "@pepdirect/v3/types";
import { generatePageId } from "@pepdirect/v3/helpers/analyticsLogger";
import { getCloudflareLoader } from "@pepdirect/v3/helpers/cloudflareImages";
import { convertFromCentsToDollars } from "@pepdirect/v3/helpers/currency";
import { META_TITLE, CANONICAL_URL_BASE } from "constants/meta";
import { endpoints, links } from "config";
import { AppContext } from "context/app";
import st from "./mission.module.scss";

const BENEFITS_TEXT = [
  {
    header: `Easy Bundles`,
    bodyText: `Shop bundled products from the brands you love & trust.`,
    imgUrl: `/static-images/pantry-shop-icons/pantryshop-curated-icon.jpg`,
  },
  {
    header: `Free Shipping`,
    bodyText: (
      freeShippingThreshold: string
    ) => `Free standard shipping over ${freeShippingThreshold} on all orders with most orders
    arriving in 2-4 business days.`,
    imgUrl: `/static-images/pantry-shop-icons/pantryshop-free-shipping-icon.jpg`,
  },
  {
    header: `Gifting`,
    bodyText: `Ship care packages to friends & family.`,
    imgUrl: `/static-images/pantry-shop-icons/pantryshop-gift-icon.jpg`,
  },
];

const FAQ_TEXT = [
  {
    headerText: `Orders & Shipping`,
    id: `ordersAndShipping`,
    faq: [
      {
        question: `What is your Return policy?`,
        answer: (
          <>
            All sales are final, we do not offer any refunds or returns. Please{" "}
            <ExternalLink href={links.contact}>contact us</ExternalLink> to let
            us know if you have any issues or concerns with your Pantry Shop
            order.
          </>
        ),
      },
      {
        question: `What is your shipping policy?`,
        answer: (freeShippingThreshold: string) => (
          <div
            dangerouslySetInnerHTML={{
              __html: `
          <ul>
            <li>Free standard shipping over ${freeShippingThreshold}</li>
            <li>
              Your order will typically arrive within 2-4 business days. Please
              note the estimated delivery date on your order confirmation page
              or updated delivery dates in email notifications.
            </li>
            <li>
              Delivery times may vary, especially during peak periods.
              Unfortunately, we don’t currently ship to Alaska or Hawaii. We do
              not ship to Canada.
            </li>
          </ul>
        `,
            }}
          ></div>
        ),
      },
      {
        question: `When can I expect to receive my order?`,
        answer: (
          <>
            <div>
              Estimated shipping time for your location is available at
              checkout. We are set up for 2-4 day delivery for most of the
              continental U.S. Delays may occur due to new constraints from
              COVID-19.
            </div>
            <div>
              Please note that in order to ensure faster delivery, your order
              may consist of multiple packages delivered on different days.
            </div>
          </>
        ),
      },
      {
        question: `How do I know if my order has shipped?`,
        answer: `When your order ships, you will receive an email notification
        to let you know that your order has shipped with the tracking number.`,
      },
      {
        question: `How do I track my order?`,
        answer: (
          <>
            <div>
              If you created an account on PantryShop.com, click on the account
              icon (profile icon at the top right-hand corner) and click on the
              Account link. Once you&apos;ve found your order, you will see a
              tracking number for your order. You can use the map on the page to
              track your order or use the tracking number provided.
            </div>
            <div>
              If you opted to check out as a Guest, please check your emails for
              order status and tracking information.
            </div>
          </>
        ),
      },
      {
        question: `Can I cancel my order?`,
        answer: (
          <>
            Due to our speedy shipping policy, Pantry Shop does not support
            order cancellation. Please reach out to us{" "}
            <ExternalLink href={links.contact}>here</ExternalLink> with any
            concerns.
          </>
        ),
      },
      {
        question: `Can I send a bundle to a friend / student / parent?`,
        answer: `Absolutely! Pantry Shop is a great solution to support the
        most important people in your life by sending products that will bring
        them joy and keep their pantry stocked. Just enter their address as the
        Delivery Address at checkout.`,
      },
    ],
  },
  {
    headerText: `Product & Nutritional Info`,
    id: `productAndNutritionalInfo`,
    faq: [
      {
        question: `Where can I find dietary and nutritional information for
        these products?`,
        answer: (
          <>
            <div>
              You can find the dietary and nutritional information for each
              bundle on the product detail page. Simply click on the left and
              right arrows in the image carousel to find the nutritional facts
              for each product.
            </div>
            <div>
              For additional information: Please see{" "}
              <ExternalLink href="https://contact.pepsico.com/quaker">
                HERE
              </ExternalLink>{" "}
              for Quaker,{" "}
              <ExternalLink href="https://contact.pepsico.com/gatorade">
                HERE
              </ExternalLink>{" "}
              for Gatorade, and{" "}
              <ExternalLink href="https://contact.pepsico.com/fritolay/faqs;itemsPerPage=50">
                HERE
              </ExternalLink>{" "}
              for Frito-Lay product information.
            </div>
          </>
        ),
      },
      {
        question: `Can I edit my bundle order?`,
        answer: `At this time, all bundles are for sale as depicted onsite. If
        you have feedback on the product or brand assortment, we’d love to hear
        it! Please let us know in the link below. `,
      },
      {
        question: `Can I change the flavors in my bundle?`,
        answer: `At this time, only the product flavors listed on each bundle
        product detail page are available for purchase.`,
      },
    ],
  },
  {
    headerText: `General`,
    id: `general`,
    faq: [
      {
        question: `Who created Pantry Shop?`,
        answer: `Pantry Shop by PepsiCo is owned and operated by PepsiCo, Inc.`,
      },
      {
        question: `Why did you create Pantry Shop by PepsiCo?`,
        answer: `As the current COVID-19 situation continues to strain the
        capacity of the North American food chain, we wanted to expand the
        pathways for PepsiCo’s loved and trusted snack and beverage brands
        to reach consumers looking for our products.
        `,
      },
      {
        question: `What else is PepsiCo doing in response to COVID-19?`,
        answer: (
          <>
            In partnership with the PepsiCo Foundation, we are activating
            PepsiCo’s global resources to provide food and other essential
            relief to help those affected by COVID-19, including donating more
            than $45 Million to fund nutrition access for out-of-school students
            and at-risk families, as well as other vital resources for those in
            need. You can read more about how we are doing our part to help
            consumers and communities{" "}
            <ExternalLink href="https://www.pepsico.com/news/story/covid-19">
              here
            </ExternalLink>
            .
          </>
        ),
      },
    ],
  },
];

const MissionPage = (): JSX.Element => {
  const { tenant } = useContext(AppContext);
  const { useLogPageView } = useContext(LogContext);
  const freeShippingThreshold = useMemo(() => {
    return convertFromCentsToDollars(
      tenant?.shippingConfig.freeShippingThreshold || 0,
      "$0,0"
    );
  }, [tenant?.shippingConfig.freeShippingThreshold]);

  // Pass the freeShippingThreshold from the tenant config to the BenefitsText and FAQText
  const BenefitsText = useMemo(() => {
    return BENEFITS_TEXT.map((benefit) => {
      return {
        header: benefit.header,
        bodyText:
          typeof benefit.bodyText === "function"
            ? benefit.bodyText(freeShippingThreshold)
            : benefit.bodyText,
        imgUrl: benefit.imgUrl,
      };
    });
  }, [freeShippingThreshold]);
  const FAQText = useMemo(() => {
    return FAQ_TEXT.map((faq) => {
      return {
        ...faq,
        faq: faq.faq.map((item) => {
          return {
            ...item,
            answer:
              typeof item.answer === "function"
                ? item.answer(freeShippingThreshold)
                : item.answer,
          };
        }),
      };
    });
  }, [freeShippingThreshold]);

  const metaTitle = `Our Mission – ${META_TITLE}`;
  const metaUrl = `${CANONICAL_URL_BASE}/our-mission`;
  const metaDescription =
    "Our mission is to bring comfort to your family by delivering your favorite snacks & beverages straight to your door";

  useLogPageView({
    pageTitle: metaTitle,
    pageType: PAGE_TYPE_ENUM.support,
    pageCategory: undefined,
    id: generatePageId("our-mission"),
  });

  return (
    <div className={st["mission--body"]}>
      <Head>
        <title>{metaTitle}</title>
        <meta name="og:title" content={metaTitle} key="og:title" />
        <link rel="canonical" href={metaUrl} />
        <meta name="og:url" content={metaUrl} key="og:url" />
        <meta name="description" content={metaDescription} key="description" />
        <meta
          name="og:description"
          content={metaDescription}
          key="og:description"
        />
      </Head>

      <section className={st["mission--section-1"]}>
        <div className={st.wrapper}>
          <Heading level="h1" size="xxl" center>
            Our Mission
          </Heading>
          <h2 className={st.subtitle}>
            Pantry Shop by PepsiCo is here to bring comfort to your family by
            delivering your favorite snacks {"&"} beverages straight to your
            door. Our online store is stocked with pantry-friendly products,
            thoughtfully curated for your household’s needs.
          </h2>
        </div>
      </section>

      {/* START SECTION-2 (BENEFITS) */}
      <section className={st["mission--section-2"]}>
        <h2 className={st["mission--header-text"]}>
          <span>Shop your favorite beverages </span>
          <span>from the comfort of your home.</span>
        </h2>
        <div className={st["mission--benefits-container"]}>
          {BenefitsText.map((benefit, index) => (
            <div className={st["mission--benefit"]} key={index}>
              <Image
                loader={getCloudflareLoader(endpoints.cdn)}
                src={benefit.imgUrl}
                alt="icon"
                width="150"
                height="150"
              />
              <h3 className={st["mission--benefit-header"]}>
                {benefit.header}
              </h3>
              <p className={st["mission--benefit-body-text"]}>
                {benefit.bodyText}
              </p>
            </div>
          ))}
        </div>
      </section>
      {/* END SECTION-2 (BENEFITS) */}
      {/* START SECTION-3 (FAQ) */}
      <section className={st["mission--section-3"]}>
        <h1 className={st.heading}>FAQs</h1>

        <div className={st.subtitle}>
          Pantry Shop is here to keep you stocked and put your mind at ease!
        </div>

        {/* TABLE OF CONTENTS */}
        {FAQText.map((header, index) => (
          <div key={`category-${index}`} className={st.category}>
            <a href={`#${header.id}`}>{header.headerText}</a>
          </div>
        ))}

        {/* FAQ */}

        {FAQText.map((faq, index) => (
          <div key={`faq-list-${index}`} id={faq.id}>
            <h2 className={st.sectionHeading}>{faq.headerText}</h2>

            <div>
              {faq.faq.map((faq, index) => (
                <div className={st.faq} key={`faq-${index}`}>
                  <h3 className={st.subHeading}>{faq.question}</h3>

                  <div className={st.content}>{faq.answer}</div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <h2 className={st.sectionHeading}>We&apos;d love to hear from you!</h2>

        <h3 className={st.subHeading}>
          Have comments or suggestions? Send them{" "}
          <ExternalLink href="https://www.surveymonkey.com/r/W3G5P5G">
            here
          </ExternalLink>
          .
        </h3>
        <h3 className={st.subHeading}>
          You can contact us{" "}
          <ExternalLink href="https://contact.pepsico.com/pantryshop">
            here
          </ExternalLink>{" "}
          for any issues.
        </h3>
      </section>
    </div>
  );
};

export default MissionPage;
